import { create } from 'zustand';
import {
  createWorkerRuntime,
  createMainThreadRuntime,
  prefetchPoseAssets,
} from '../workers/pose-runtime';

// Ordered fallback ladder. The store attempts each rung in turn and keeps the
// first that successfully builds a PoseLandmarker:
//
//   1. Worker + GPU  — best case (desktop, Android): inference off the main
//                      thread, hardware accelerated.
//   2. Worker + CPU  — worker context is fine but WebGL2-in-worker isn't.
//   3. Main + GPU    — worker context can't build the WASM graph at all (the
//                      iOS Safari "Can't find variable: document" failure), so
//                      run on the main thread where `document` exists.
//   4. Main + CPU    — final safety net.
//
// Because the whole ladder is walked before the user ever sees a failure, the
// "Couldn't prepare the exercise" screen only appears when even main-thread+CPU
// failed (a genuine hard failure — e.g. assets couldn't be fetched at all).
const STRATEGIES = [
  { kind: 'worker', delegate: 'GPU' },
  { kind: 'worker', delegate: 'CPU' },
  { kind: 'main', delegate: 'GPU' },
  { kind: 'main', delegate: 'CPU' },
];

// Module-scoped so a remount of any consumer reuses the single built runtime
// instead of rebuilding it.
let runtimeInstance = null;
let buildPromise = null;
let prefetchPromise = null;

async function buildRuntime(onProgress) {
  const failures = [];

  for (const strategy of STRATEGIES) {
    onProgress({ phase: 'wasm', loaded: 0, total: 0 });
    try {
      const runtime =
        strategy.kind === 'worker'
          ? await createWorkerRuntime({ delegate: strategy.delegate, onProgress })
          : await createMainThreadRuntime({ delegate: strategy.delegate, onProgress });
      return { runtime, strategy };
    } catch (error) {
      const label = `${strategy.kind}/${strategy.delegate}`;
      const message = error?.message || String(error);
      failures.push(`${label}: ${message}`);
      // eslint-disable-next-line no-console
      console.warn(`Pose runtime rung "${label}" failed, trying next.`, error);
    }
  }

  const aggregate = new Error(failures[failures.length - 1] || 'All pose runtimes failed to load');
  aggregate.details = failures;
  throw aggregate;
}

export const usePoseWorkerStore = create((set, get) => ({
  // The active pose runtime handle ({ kind, delegate, detect, warmup, dispose })
  // once a ladder rung succeeds — consumed by the exercise detection loop.
  runtime: null,
  // Which rung won, for diagnostics/telemetry (e.g. { kind:'main', delegate:'CPU' }).
  activeRuntime: null,
  isReady: false,
  error: null,
  // Real download progress. `phase` is 'wasm' while the WASM fileset loads (no
  // byte signal) then 'model' with real loaded/total.
  progress: { phase: 'wasm', loaded: 0, total: 0 },

  // App startup: only warm the model + WASM cache, do NOT build a PoseLandmarker
  // (which is expensive and can fail per-device). The exercise page builds the
  // real runtime later via ensureRuntime(). Idempotent + never throws.
  prefetchAssets() {
    if (prefetchPromise) {
      return prefetchPromise;
    }
    prefetchPromise = prefetchPoseAssets().catch(() => {});
    return prefetchPromise;
  },

  // Exercise preparation: build a runtime by walking the fallback ladder. Safe
  // to call repeatedly — returns the in-flight build or the already-built
  // runtime.
  ensureRuntime() {
    if (runtimeInstance) {
      return Promise.resolve(runtimeInstance);
    }
    if (buildPromise) {
      return buildPromise;
    }

    set({ error: null, isReady: false, progress: { phase: 'wasm', loaded: 0, total: 0 } });

    const onProgress = update =>
      set({
        progress: {
          phase: update.phase,
          loaded: update.loaded ?? 0,
          total: update.total ?? 0,
        },
      });

    buildPromise = buildRuntime(onProgress)
      .then(({ runtime, strategy }) => {
        runtimeInstance = runtime;
        set({ runtime, activeRuntime: strategy, isReady: true, error: null });
        return runtime;
      })
      .catch(error => {
        buildPromise = null;
        set({
          runtime: null,
          activeRuntime: null,
          isReady: false,
          error: error?.message || 'Failed to load MediaPipe Pose',
        });
        return null;
      });

    return buildPromise;
  },

  // Re-attempt after a failure. Unlike the old retry (which re-ran the identical
  // failing strategy), this tears down any partial runtime and re-walks the
  // whole ladder from the top — so a transient failure (e.g. flaky asset fetch)
  // gets a fresh shot at every rung, and a device that can only run on the main
  // thread ends up there.
  retry() {
    if (runtimeInstance) {
      try {
        runtimeInstance.dispose?.();
      } catch (error) {
        // ignore teardown failures
      }
      runtimeInstance = null;
    }
    buildPromise = null;
    set({
      runtime: null,
      activeRuntime: null,
      error: null,
      isReady: false,
      progress: { phase: 'wasm', loaded: 0, total: 0 },
    });
    return get().ensureRuntime();
  },
}));
